import React, { useEffect, useState, useCallback } from 'react';
import { adminAPI } from '../../lib/adminAPI';
import { useToast } from '../../hooks/use-toast';
import { Mail, Save, RefreshCw, CheckCircle2, XCircle, ArrowDownToLine, ArrowUpToLine } from 'lucide-react';

const EVENT_META = {
  admin_new_deposit: { label: 'Admin alert · new deposit', icon: ArrowDownToLine, color: '#0ea5e9' },
  admin_new_withdraw: { label: 'Admin alert · new withdrawal', icon: ArrowUpToLine, color: '#f97316' },
  user_deposit_completed: { label: 'User · deposit approved', icon: CheckCircle2, color: '#00e676' },
  user_deposit_rejected: { label: 'User · deposit rejected', icon: XCircle, color: '#ef4444' },
  user_withdraw_completed: { label: 'User · withdrawal approved', icon: CheckCircle2, color: '#00e676' },
  user_withdraw_rejected: { label: 'User · withdrawal rejected', icon: XCircle, color: '#ef4444' },
  admin_daily_digest: { label: 'Admin · daily digest', icon: Mail, color: '#a855f7' }
};

const AdminEmailNotifications = () => {
  const [enabled, setEnabled] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const loadLog = useCallback(async () => {
    try { setLog(await adminAPI.emailLog(100)); } catch {}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const s = await adminAPI.getEmailSettings();
      setEnabled(!!s.enabled);
      setAdminEmail(s.admin_email || '');
      setDigestEnabled(!!s.digest_enabled);
    } catch {}
    await loadLog();
    setLoading(false);
  }, [loadLog]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live feed: poll the sent-log every 15s
  useEffect(() => {
    const t = setInterval(loadLog, 15000);
    return () => clearInterval(t);
  }, [loadLog]);

  const save = async () => {
    if (enabled && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(adminEmail.trim())) {
      toast({ title: 'Recipient email required', description: 'Enter a valid admin email to receive alerts.' });
      return;
    }
    setSaving(true);
    try {
      const s = await adminAPI.setEmailSettings({ enabled, admin_email: adminEmail.trim(), digest_enabled: digestEnabled });
      setEnabled(!!s.enabled); setAdminEmail(s.admin_email || ''); setDigestEnabled(!!s.digest_enabled);
      toast({ title: 'Email settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-email-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Email Alerts</h1>
        <p className="text-sm text-gray-400">Get emailed the moment a user requests a deposit or withdrawal, and auto-email users when you approve or reject them.</p>
      </div>

      {/* Settings panel */}
      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden max-w-2xl">
        <div className="px-4 py-3 border-b border-[#141f1b] flex items-center gap-2">
          <Mail size={14} className="text-[#00e676]"/>
          <span className="text-sm font-semibold text-white">Notification Settings</span>
        </div>
        {loading ? (
          <div className="py-10 text-center text-gray-500 text-sm">Loading…</div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-white font-medium">Enable email notifications</div>
                <div className="text-xs text-gray-500">Master switch for all deposit/withdrawal emails.</div>
              </div>
              <button
                onClick={() => setEnabled(v => !v)}
                data-testid="email-enabled-toggle"
                role="switch" aria-checked={enabled}
                className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-[#00e676]' : 'bg-[#1a2823]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : ''}`}/>
              </button>
            </div>

            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">Admin recipient email</label>
              <input
                type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)}
                placeholder="alerts@yourbusiness.com"
                data-testid="admin-email-input"
                className="mt-1.5 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#00e676]"
              />
              <p className="mt-1.5 text-xs text-gray-500">Where new deposit &amp; withdrawal request alerts are sent. Users are auto-emailed on their own address when you approve/reject.</p>
            </div>

            <div className="flex items-center justify-between border-t border-[#141f1b] pt-4">
              <div>
                <div className="text-sm text-white font-medium">Daily digest summary</div>
                <div className="text-xs text-gray-500">Email the admin recipient one daily summary of pending deposits, withdrawals &amp; KYC (8am UTC).</div>
              </div>
              <button
                onClick={() => setDigestEnabled(v => !v)}
                data-testid="digest-enabled-toggle"
                role="switch" aria-checked={digestEnabled}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${digestEnabled ? 'bg-[#00e676]' : 'bg-[#1a2823]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${digestEnabled ? 'translate-x-6' : ''}`}/>
              </button>
            </div>

            <button onClick={save} disabled={saving} data-testid="save-email-btn" className="btn-green flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm disabled:opacity-50">
              <Save size={15}/> {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>

      {/* Live sent log */}
      <div className="rounded-xl border border-[#1a2823] bg-[#0a1210] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#141f1b] flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00e676] opacity-75"/>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00e676]"/>
          </span>
          <span className="text-sm font-semibold text-white">Live Sent Log</span>
          <span className="text-xs text-gray-500">({log.length})</span>
          <button onClick={loadLog} data-testid="refresh-email-log" className="ml-auto text-gray-400 hover:text-[#00e676]"><RefreshCw size={14}/></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] text-gray-500 uppercase tracking-wider">
              <th className="px-4 py-3">Event</th><th>Recipient</th><th>Amount</th><th>Status</th><th className="pr-4">When</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan="5" className="py-8 text-center text-gray-500">Loading…</td></tr>}
              {!loading && log.length === 0 && <tr><td colSpan="5" className="py-10 text-center text-gray-500">No emails sent yet. Enable notifications and a deposit/withdrawal will show here.</td></tr>}
              {log.map(e => {
                const meta = EVENT_META[e.event] || { label: e.event, icon: Mail, color: '#8aa39b' };
                const Ico = meta.icon;
                return (
                  <tr key={e.id} className="border-t border-[#141f1b] hover:bg-[#0f1a17]" data-testid={`email-log-${e.id}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2 text-white font-medium">
                        <span className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}1a`, color: meta.color }}><Ico size={13}/></span>
                        {meta.label}
                      </span>
                    </td>
                    <td className="text-gray-300">{e.to}</td>
                    <td className="text-gray-300 mono">{e.amount != null ? `$${Number(e.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}</td>
                    <td>
                      {e.status === 'sent'
                        ? <span className="inline-flex items-center gap-1 text-[#00e676] text-xs"><CheckCircle2 size={12}/> Sent</span>
                        : <span className="inline-flex items-center gap-1 text-red-400 text-xs" title={e.error || ''}><XCircle size={12}/> Failed</span>}
                    </td>
                    <td className="pr-4 text-xs text-gray-400 mono whitespace-nowrap">{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailNotifications;
